import { API_ROUTES } from 'networking/api-routes';
import { ApiService } from 'networking/api-service';
import type { OnboardingVariant } from 'common/returning-onboarding/onboarding-copy';

type OnboardingImageRaw = {
  id: number;
  album_id: number;
  kind: 'photo' | 'video';
  name: string | null;
  image_url: string;
  duration: number | null;
};

type OnboardingRaw = {
  onboarding: {
    status: OnboardingStatus;
    variant: OnboardingVariant;
    can_add_memories?: boolean;
    vault: {
      id: number;
      title: string;
      memories_count: number;
      provider_name: string | null;
      landing_path: string | null;
    } | null;
    images: OnboardingImageRaw[];
  };
};

export type OnboardingImage = {
  id: number;
  albumId: number;
  kind: 'photo' | 'video';
  name: string;
  imageUrl: string;
  // "2:34", or empty when unknown (photos).
  duration: string;
};

export type OnboardingSnapshot = {
  status: OnboardingStatus;
  variant: OnboardingVariant;
  // False for viewers: the introduction has no New step for them.
  canAddMemories: boolean;
  vault: {
    id: number;
    title: string;
    memoriesCount: number;
    providerName: string | null;
    // Set when only an album or memory was shared: the introduction ends
    // there, not on a dashboard they can't use.
    landingPath: string | null;
  } | null;
  images: OnboardingImage[];
};

const formatDuration = (seconds: number | null) => {
  if (!seconds || seconds <= 0) return '';
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = String(total % 60).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}`
    : `${minutes}:${rest}`;
};

class OnboardingController {
  // The vault with the most memories the user can see, and up to four of its
  // pictures.
  static async get(): Promise<OnboardingSnapshot> {
    const response = await ApiService.get<OnboardingRaw>(API_ROUTES.ONBOARDING);
    const { status, variant, vault, images } = response.data.onboarding;
    return {
      status,
      variant,
      canAddMemories: response.data.onboarding.can_add_memories ?? false,
      vault: vault
        ? {
            id: vault.id,
            title: vault.title,
            memoriesCount: vault.memories_count,
            providerName: vault.provider_name,
            landingPath: vault.landing_path,
          }
        : null,
      images: images.map((image) => ({
        id: image.id,
        albumId: image.album_id,
        kind: image.kind,
        name: image.name ?? '',
        imageUrl: image.image_url,
        duration: formatDuration(image.duration),
      })),
    };
  }

  // Only moves forward; returns the status the server keeps.
  static async advance(status: OnboardingStatus): Promise<OnboardingStatus> {
    const response = await ApiService.put<{
      onboarding: { status: OnboardingStatus };
    }>(API_ROUTES.ONBOARDING, { status });
    return response.data.onboarding.status;
  }
}

export { OnboardingController };
